/* stylelint-disable property-no-vendor-prefix */
import {forwardRef, RefObject, useCallback, useMemo, useState, FocusEvent} from 'react';

import styled, {css} from 'styled-components';
import {isEmpty} from 'lodash';

import clearTextIconUrl from '~/assets/images/icons/clear_text.svg';
import {flexBottomVertically} from '~/shared/theme/FlexLayout';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
 
import {flipOnLTR} from '../../../theme/utils';

import FormInputActionButton from './FormInputActionButton';
import Label from './FormInputLabel';
import {FormInputProps} from './types';

// #region styled components
const RootBase = styled.div<{
  direction?: 'ltr' | 'rtl';
  withBorder?: boolean;
  isFocused?: boolean;
  hasError?: boolean;
}>`
  position: relative;
  width: 100%;
  ${({theme, direction}) => (direction === 'ltr' || theme.direction === 'ltr' ? `
    padding-left: 8px;
    text-align: left;  
  ` : `
    padding-right: 8px;
    text-align: right;
  `)}
  background-color: ${({theme}) => theme.colors.surface};
  ${flexBottomVertically}
  ${({withBorder, theme}) => withBorder && `
    border: 1px solid ${theme.colors.gray500};
  `}
  ${({isFocused, theme}) => isFocused && `
    border: solid 1px ${theme.colors.text};
  `}
  ${({hasError, theme}) => hasError && `
    border: solid 1px ${theme.colors.error};
  `}
`;

const floatLabelInputRootStyles = css`
  padding-bottom: 5px;
`;

const noFloatLabelInputRootStyles = css`
  display: flex;
  align-items: center;
`;

const BasicInputRoot = styled(RootBase)<{removeFloatLabel?: boolean}>`
  height: 48px;
  ${({removeFloatLabel}) => (removeFloatLabel ? noFloatLabelInputRootStyles : floatLabelInputRootStyles)}
`;

const BasicInput = styled.input<{direction?: 'ltr' | 'rtl'}>`
  width: 100%;
  height: 20px;
  border: none;
  outline: none;
  padding: 0;
  ${({direction}) => direction && `direction: ${direction};`}

  &&& {
    color: ${({theme}) => theme.colors.secondary};
    font-size: 16px;
    line-height: 20px;
  }

  &:disabled {
    background-color: transparent;
    opacity: 0.5;
  }

  ${({theme, type}) =>
    (type === 'number' ?
      `
    ::-webkit-inner-spin-button, 
    ::-webkit-outer-spin-button { 
      -webkit-appearance: none; 
      margin: 0; 
    }
    -moz-appearance: textfield;
  ` : type === 'tel' ? `
      ${`direction: ${theme.direction};`}
  ` : '')};

  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    border: none;
    -webkit-box-shadow: 0 0 0 30px ${({theme}) => theme.colors.surface} inset !important;
    -webkit-text-fill-color: ${({theme}) => theme.colors.secondary} !important;

    ~ div {
      top: 6px;
      font-size: 9px;
    }
  }
`;

const ClearButton = styled(ImageWithAlt).attrs({
  src: clearTextIconUrl,
})`
  position: absolute;
  top: 50%;
  margin-top: -7.5px;
  ${flipOnLTR`
    left: 10px;
  `};
  width: 15px;
`;

const TextAreaRoot = styled(RootBase)`
  height: 100%;
  min-height: initial;
  padding: 22px 0 0 0;
`;

const TextArea = styled.textarea`
  height: 100%;
  width: 100%;
  border: 0;
  outline: none;
  resize: none;
  color: ${({theme}) => theme.colors.secondary};
  font-size: 13px;
  padding: 0 10px !important;
`;
// #endregion

const isForwardedHTMLInputRef = (ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>): ref is RefObject<HTMLInputElement> => !!ref &&
  typeof ref !== 'function' &&
  'current' in ref &&
  (ref.current instanceof HTMLInputElement || ref.current === null);

const isForwardedHTMLTextAreaRef = (ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>): ref is RefObject<HTMLTextAreaElement> => !!ref &&
  typeof ref !== 'function' &&
  'current' in ref &&
  (ref.current instanceof HTMLTextAreaElement || ref.current === null);

const FormInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, FormInputProps>(
  (
    {
      type = 'text',
      selectOnFocus,
      onFocus,
      onBlur,
      value,
      hasError,
      className,
      required,
      id,
      disabled,
      onClearButtonClick,
      placeholder,
      active,
      actionButton,
      withBorder,
      inputPlaceholder,
      direction,
      fixedFloatLabel,
      labelStyleOverrides,
      omitStarSymbolOnRequired,
      removeFloatLabel,
      ...nativeInputProps
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const isTextArea = useMemo(() => type === 'textarea', [type]);

    const handleFocus = useCallback((event: FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(event);
      }
      if (selectOnFocus && isForwardedHTMLInputRef(ref)) {
        ref.current?.select();
      }
    }, [onFocus, selectOnFocus, ref]);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onBlur?.();
    }, [onBlur]);

    const Root = isTextArea ? TextAreaRoot : BasicInputRoot;

    const inputComponentProps = {
      ...nativeInputProps,
      id,
      type,
      required,
      disabled,
      onFocus: handleFocus,
      onBlur: handleBlur,
      value,
      placeholder: inputPlaceholder,
    };

    return (
      <Root {...{isFocused, hasError, removeFloatLabel}} withBorder={withBorder} direction={direction} className={className ? `${className} form-input` : 'form-input'}>
        {isTextArea ? (
          <TextArea ref={isForwardedHTMLTextAreaRef(ref) ? ref : undefined} {...inputComponentProps} />
        ) : (
          <BasicInput ref={isForwardedHTMLInputRef(ref) ? ref : undefined} {...inputComponentProps} direction={direction} />
        )}
        {!removeFloatLabel && (
          <Label
            htmlFor={id}
            error={hasError}
            disabled={disabled}
            float={!!value || isFocused || fixedFloatLabel}
            required={required}
            text={placeholder}
            omitStarSymbolOnRequired={omitStarSymbolOnRequired}
            labelStyleOverrides={labelStyleOverrides}
          />
        )}
        {isTextArea && onClearButtonClick && <ClearButton onClick={onClearButtonClick} />}
        {!active && !isEmpty(actionButton) && <FormInputActionButton actionButton={actionButton} value={value} />}
      </Root>
    );
  },
);

export default FormInput;
